import { motion } from "framer-motion";
import { Quote, Star } from "lucide-react";

const featured = {
  quote:
    "Your passion for the subject is evident and has greatly enhanced my learning experience. The way you explain complex concepts with clarity and patience is particularly helpful, and the way you incorporate real-world examples makes the material relatable and easy to understand. The structured approach to each class encourages critical thinking — I have thoroughly enjoyed the class and look forward to applying what I have learned in future engagements.",
  name: "Pierre H. Ndongue",
  role: "Microsoft Azure Administration",
  location: "Milwaukee, US",
};

const testimonials = [
  {
    quote:
      "A must-take course for anyone who doesn't have much experience with cloud administration. Ajay explains concepts well and has a perfect mix of theory and hands-on lab demonstrations.",
    name: "Shaun Fernandes",
    role: "Microsoft Azure Administration",
    location: "College Park, US",
  },
  {
    quote:
      "I would like to thank Ajay sir for making the sessions very informative and practical. The way he explained Windows Server, domain setup, folder redirection, Linux and troubleshooting was very clear. The hands-on labs gave us real-time experience and helped me build a strong foundation.",
    name: "Varsha Poornima",
    role: "IT Infrastructure — Enterprise Batch",
    location: "Wipro · India",
  },
  {
    quote:
      "This was one of the best training programs I have attended. Ajay is a subject-matter expert who really knows how to explain things. I will come back for additional training.",
    name: "Efrem Araya",
    role: "Microsoft Azure Administration",
    location: "Phoenix, US",
  },
  {
    quote:
      "Ajay sir, the program was a wonderful experience. I gained not only technical knowledge but practical skills that will help me in the future. Your way of teaching made it easy to understand and motivated me to keep learning.",
    name: "Mahetab Shaikh",
    role: "Data Centre & Cloud Foundations",
    location: "Wipro · India",
  },
  {
    quote:
      "The Azure course is designed not just for the exam but for real hands-on practice. Ajay has a deep understanding of Azure infrastructure and is a very good trainer.",
    name: "Arpita Mehta",
    role: "Microsoft Azure Administration",
    location: "Troy, US",
  },
  {
    quote:
      "Hands-on experience helped me gain practical knowledge and confidence. With Ajay sir's guidance, I was able to overcome challenges and stay focused under pressure throughout the program.",
    name: "Tejaswini Tarigonda",
    role: "Data Centre & Cloud Foundations",
    location: "Wipro · India",
  },
  {
    quote:
      "I learned a lot from this Azure training. The course content and trainer are excellent — Ajay motivated me to take the Microsoft certification exam next week.",
    name: "Aunkon Khan",
    role: "Microsoft Azure Administration",
    location: "Huntington Station, US",
  },
  {
    quote:
      "Very good training that covered the Azure infrastructure in detail. I will be coming back for Azure data-engineering training soon.",
    name: "Manpreet Singh",
    role: "Microsoft Azure Administration",
    location: "Adelaide, AU",
  },
];

const Stars = () => (
  <div className="flex items-center gap-0.5 text-amber-400">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={12} className="fill-current" strokeWidth={0} />
    ))}
  </div>
);

export default function Testimonials() {
  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-8 bg-white/30" />
          <span className="label-eyebrow">04 — Voices</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-12 items-end">
          <h2 className="md:col-span-8 font-display font-bold text-3xl sm:text-4xl md:text-5xl tracking-tight text-white">
            What learners say <br />
            <span className="text-slate-500">after the program.</span>
          </h2>
          <p className="md:col-span-4 text-slate-400 leading-relaxed">
            Verbatim feedback from learners across the US, Canada, UK, India and
            the GCC — from enterprise batches to one-to-one Azure mentorships.
          </p>
        </div>

        {/* Featured testimonial */}
        <motion.figure
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          data-testid="featured-testimonial"
          className="relative bg-gradient-to-br from-[#0E1424] to-[#0A0C13] border border-white/10 rounded-3xl p-8 md:p-12 overflow-hidden mb-6"
        >
          <Quote className="absolute top-8 right-8 text-blue-500/30" size={56} />
          <div className="relative max-w-3xl">
            <Stars />
            <blockquote className="mt-5 text-xl md:text-2xl text-slate-100 font-display font-medium leading-snug tracking-tight">
              "{featured.quote}"
            </blockquote>
            <figcaption className="mt-8 flex items-center gap-4 pt-6 border-t border-white/10">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500/30 to-cyan-400/20 border border-white/10 grid place-items-center font-display font-bold text-white">
                {featured.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
              </div>
              <div>
                <div className="font-medium text-white">{featured.name}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 mt-0.5">
                  {featured.role} · {featured.location}
                </div>
              </div>
            </figcaption>
          </div>
        </motion.figure>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {testimonials.map((t, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.55, delay: i * 0.06 }}
              className="bg-[#0A0C13] border border-white/10 rounded-2xl p-7 hover:border-white/25 transition-colors flex flex-col"
              data-testid={`testimonial-${i}`}
            >
              <Stars />
              <blockquote className="mt-4 text-slate-200 text-[15px] leading-relaxed flex-1 font-body">
                "{t.quote}"
              </blockquote>
              <figcaption className="mt-6 pt-5 border-t border-white/10 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500/25 to-cyan-400/15 border border-white/10 grid place-items-center font-display font-semibold text-white text-sm">
                  {t.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-white text-sm truncate">{t.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-0.5 truncate">
                    {t.role}
                  </div>
                  <div className="font-mono text-[10px] tracking-[0.18em] text-blue-300/80 mt-0.5 truncate">
                    {t.location}
                  </div>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        {/* Footer note */}
        <div className="mt-10 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
          <div className="font-mono uppercase tracking-[0.22em]">
            2,150+ learners · 6,500+ training hours · 26+ batches delivered
          </div>
          <div className="font-mono uppercase tracking-[0.22em] text-blue-300/70">
            US · CA · UK · IN · GCC · AU
          </div>
        </div>
      </div>
    </section>
  );
}
